export type ElementDimensions = Readonly<{ height: number; width: number }>;
type Listener = (dimensions: ElementDimensions) => void;
type Observation = { listeners: Set<Listener>; dimensions?: ElementDimensions };

const observations = new Map<Element, Observation>();
let observer: ResizeObserver | undefined;

/** One lazy observer for extension telemetry. Never reads layout or updates React state. */
export function observeElementDimensions(
	element: Element,
	listener: Listener,
): (() => void) | undefined {
	if (typeof window === 'undefined' || !window.ResizeObserver) {
		return;
	}
	observer ??= new ResizeObserver((entries) => {
		for (const {
			target,
			contentRect: { height, width },
		} of entries) {
			const observation = observations.get(target);
			if (
				!observation ||
				!Number.isFinite(height) ||
				!Number.isFinite(width) ||
				height < 0 ||
				width < 0
			) {
				continue;
			}
			const dimensions = { height: Math.round(height), width: Math.round(width) };
			if (
				observation.dimensions?.height === dimensions.height &&
				observation.dimensions?.width === dimensions.width
			) {
				continue;
			}
			observation.dimensions = dimensions;
			observation.listeners.forEach((notify) => notify(dimensions));
		}
	});
	let observation = observations.get(element);
	if (!observation) {
		observation = { listeners: new Set() };
		observations.set(element, observation);
		observer.observe(element);
	}
	observation.listeners.add(listener);
	if (observation.dimensions) {
		listener(observation.dimensions);
	}
	return () => {
		if (!observation.listeners.delete(listener) || observation.listeners.size) {
			return;
		}
		observer?.unobserve(element);
		observations.delete(element);
		if (!observations.size) {
			observer?.disconnect();
			observer = undefined;
		}
	};
}
